import {
	Checkbox,
	Icon,
	TagList,
	TagModel
} from "client/Components";
import { CardAppIcon } from "../CardAppIcon/CardAppIcon";
import styles from "./AddTileCard.module.less";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { categoryToNameMap } from "../CategoryFilter";
import { appKindToNameMap } from "../AppCard";
import { localize } from "client_legacy/ndaw/culture";
import classNames from "classnames";
import { setTrackingId } from "client/Misc/utils";
import { AvailableAppModel } from "client/Api/Workspace/workspace";
import { TagViewModel } from "client/Api/Tags/tags";

export type TileCardProps = {
	tile: AvailableAppModel;
	onClick: (appId: number) => void;
	isSelected: boolean;
};

export const AddTileCard = (props: TileCardProps): ReactElement => {
	const { isSelected, onClick, tile } = props;

	const categoryName: LocalizationKey = categoryToNameMap[tile.appCategory];
	const typeName: string = appKindToNameMap[tile.appType];

	const toggleCheckbox = useCallback(
		(): void => {
			onClick(tile.installedAppId);
		},
		[onClick, tile]
	);

	const { isAccessible, tileWillBeHiddenWhenNotAccessible, reason } =
		tile.accessibilityState;

	const tooltipText = reason ?? "";

	const tagModels = useMemo(
		(): readonly TagModel<number>[] => {
			return tile.tags
				.map((tag: TagViewModel): TagModel<number> => ({
					text: tag.name,
					value: tag.id,
					color: "secondary"
				}));
		},
		[tile.tags]
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_active]: isSelected,
				})}
			{...setTrackingId("add-tile-card")}
		>
			<div className={styles.iconContainer}>
				<CardAppIcon appColor={tile.color} icon={tile.image} />
			</div>
			<h4
				{...setTrackingId("name")}
				className={styles.name}
				title={tile.name}
			>
				{tile.name}
			</h4>
			<p
				{...setTrackingId("type-name")}
				className={styles.typeName}
			>
				{typeName}
			</p>
			<p
				{...setTrackingId("category-name")}
				className={styles.categoryName}
			>
				{localize(categoryName)}
			</p>
			<div className={styles.tagsContainer}>
				<TagList
					tags={tagModels}
					trackingId="tags"
					showMoreTagsColor="secondary"
				/>
			</div>
			{!isAccessible && (
				<div className={styles.accessibilityContainer}>
					{tileWillBeHiddenWhenNotAccessible ? (
						<div className={styles.icon} title={tooltipText}>
							<Icon value="eye-slash" />
						</div>
					) : (
						<div className={styles.icon} title={tooltipText}>
							<Icon value="ban" />
						</div>
					)}
				</div>
			)}
			<div className={styles.checkbox}>
				<Checkbox
					value={isSelected}
					onChange={toggleCheckbox}
					trackingId="add-tile-card-checkbox"
				/>
			</div>
		</div>
	);
};

AddTileCard.displayName = "AddTileCard";
